/**
 * PIN Crypto Utilities
 *
 * Handles secure PIN storage and verification for the Lock on Resume feature.
 * Uses SHA-256 hashing via crypto.subtle for secure storage.
 */

const PIN_STORAGE_PREFIX = 'appLock_pin_';

interface StoredPinData {
  hash: string;
  salt: string;
  createdAt: number;
}

/**
 * Generate a random salt for PIN hashing
 */
function generateSalt(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash a PIN with the given salt using SHA-256
 */
export async function hashPin(pin: string, salt: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + salt);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Store a hashed PIN for a user in localStorage
 */
export async function storePinHash(userId: string, pin: string): Promise<void> {
  const salt = generateSalt();
  const hash = await hashPin(pin, salt);

  const data: StoredPinData = {
    hash,
    salt,
    createdAt: Date.now(),
  };

  localStorage.setItem(`${PIN_STORAGE_PREFIX}${userId}`, JSON.stringify(data));
}

/**
 * Verify a PIN against the stored hash
 */
export async function verifyPin(userId: string, pin: string): Promise<boolean> {
  const storedData = localStorage.getItem(`${PIN_STORAGE_PREFIX}${userId}`);

  if (!storedData) {
    return false;
  }

  try {
    const data: StoredPinData = JSON.parse(storedData);
    const hash = await hashPin(pin, data.salt);
    return hash === data.hash;
  } catch (error) {
    console.error('Error verifying PIN:', error);
    return false;
  }
}

/**
 * Check if a PIN has been set up for a user
 */
export function hasPinSetup(userId: string): boolean {
  return localStorage.getItem(`${PIN_STORAGE_PREFIX}${userId}`) !== null;
}

/**
 * Clear the stored PIN for a user
 */
export function clearPin(userId: string): void {
  localStorage.removeItem(`${PIN_STORAGE_PREFIX}${userId}`);
}

/**
 * Validate PIN format and security
 * Returns an error message if invalid, or null if valid
 */
export function validatePin(pin: string): string | null {
  // Must be exactly 4 digits
  if (!/^\d{4}$/.test(pin)) {
    return 'PIN must be 4 digits';
  }

  // Check for sequential patterns (1234, 4321, etc.)
  const sequential = ['0123', '1234', '2345', '3456', '4567', '5678', '6789', '7890'];
  const reverseSequential = sequential.map(s => s.split('').reverse().join(''));

  for (const pattern of [...sequential, ...reverseSequential]) {
    if (pin === pattern) {
      return 'PIN cannot be sequential numbers';
    }
  }

  // Check for repeated digits (1111, 2222, etc.)
  const allSameDigit = /^(\d)\1{3}$/.test(pin);
  if (allSameDigit) {
    return 'PIN cannot be all the same digit';
  }

  return null;
}
